import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ArrowLeft, CheckCircle, Phone, User, MapPin, FileText, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return "https://placehold.co/100x100/e2e8f0/64748b?text=P";
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const formatPrice = (price) => {
  return `L ${price.toLocaleString("es-HN", { minimumFractionDigits: 2 })}`;
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_address: "",
    notes: "",
  });

  useEffect(() => {
    const saved = localStorage.getItem("shop_cart");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.length === 0) {
        navigate("/tienda");
      }
      setCart(parsed);
    } else {
      navigate("/tienda");
    }
  }, [navigate]);

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_phone) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/shop/orders`, {
        ...formData,
        items: cart.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
        })),
      });

      setOrderData(response.data);
      setOrderComplete(true);
      localStorage.removeItem("shop_cart");
      toast.success("¡Pedido realizado con éxito!");
    } catch (error) {
      toast.error("Error al procesar el pedido");
    } finally {
      setLoading(false);
    }
  };

  if (orderComplete && orderData) {
    return (
      <Layout>
        <section className="py-16 bg-gray-50 min-h-[60vh]">
          <div className="max-w-2xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-8 text-center shadow-lg"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-brand-text mb-2">
                ¡Pedido Confirmado!
              </h1>
              <p className="text-brand-textSecondary mb-6">
                Tu pedido ha sido recibido y será procesado pronto.
              </p>

              <div className="bg-gray-50 rounded-xl p-6 mb-6 text-left">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Número de pedido:</span>
                  <span className="font-bold text-primary">{orderData.order_number}</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Total:</span>
                  <span className="font-bold text-xl">{formatPrice(orderData.total)}</span>
                </div>
                <div className="border-t pt-4 mt-4">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Nombre:</strong> {orderData.customer_name}
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Teléfono:</strong> {orderData.customer_phone}
                  </p>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-amber-800 text-sm">
                  <strong>Nota:</strong> Nos comunicaremos contigo al número {orderData.customer_phone} para coordinar el pago y la entrega.
                </p>
              </div>

              <Link to="/tienda">
                <Button className="rounded-full">
                  Seguir Comprando
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Header */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/tienda" className="inline-flex items-center gap-2 text-gray-600 hover:text-primary mb-4">
            <ArrowLeft className="h-4 w-4" />
            Volver a la tienda
          </Link>
          <h1 className="font-heading text-3xl font-bold text-brand-text">
            Finalizar Pedido
          </h1>
        </div>
      </section>

      {/* Checkout Form */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Datos de Contacto</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-2">
                          <User className="h-4 w-4" />
                          Nombre Completo *
                        </Label>
                        <Input
                          value={formData.customer_name}
                          onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                          placeholder="Tu nombre"
                          required
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2">
                          <Phone className="h-4 w-4" />
                          Teléfono *
                        </Label>
                        <Input
                          value={formData.customer_phone}
                          onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                          placeholder="+504 9999-9999"
                          required
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>Correo Electrónico (opcional)</Label>
                      <Input
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                        placeholder="tu@email.com"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Dirección de Entrega
                      </Label>
                      <Textarea
                        value={formData.customer_address}
                        onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                        placeholder="Dirección completa para la entrega"
                        className="mt-1"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label className="flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Notas del Pedido
                      </Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder="Instrucciones especiales, horarios de entrega, etc."
                        className="mt-1"
                        rows={3}
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-blue-800 text-sm">
                        <strong>Forma de Pago:</strong> Nos comunicaremos contigo para coordinar el pago. Aceptamos efectivo, transferencia bancaria y pago móvil.
                      </p>
                    </div>

                    <Button type="submit" className="w-full rounded-full" size="lg" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Confirmar Pedido
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ShoppingBag className="h-5 w-5" />
                    Resumen del Pedido
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.product_id} className="flex gap-3">
                        <img
                          src={getImageUrl(item.featured_image_url)}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-brand-text truncate">
                            {item.name}
                          </h4>
                          <p className="text-sm text-gray-500">
                            {item.quantity} x {formatPrice(item.price)}
                          </p>
                          <p className="text-sm font-bold text-primary">
                            {formatPrice(item.price * item.quantity)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t mt-6 pt-6 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span>{formatPrice(total)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Envío</span>
                      <span className="text-green-600">A coordinar</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total</span>
                      <span className="text-primary">{formatPrice(total)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
